import React, { Fragment, useEffect, useState } from "react";
import moment from "moment";
import axios from "axios";
import { useSelector } from "react-redux";
import { useFormik, Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faNote,
    faMessageLines,
    faEnvelope,
    faBrowser,
    faBoltLightning,
    faPaperclipVertical,
    faCheck,
    faX,
    faCircle,
    faCloudUploadAlt,
    faClock,
    faStickyNote,
    faImages,
} from "@fortawesome/pro-regular-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { useDispatch } from "react-redux";
import { openModal } from "../../store/modal/modalReducer";

import NotificationToast from "../Helpers/NotificationToast";
import BookingTextArea from "./BookingTextArea";
import BookingWysiwyg from "./BookingWysiwyg";
import BookingTemplate from "./BookingTemplate";
import BookingPlatformButton from "./BookingPlatformButton";
import SubmitButton from "../Forms/SubmitButton";
import BookingFileUpload from "../Forms/BookingFileUpload";
import EmojiPicker from "../Forms/EmojiPicker";
import BookingPlaceholder from "./BookingPlaceholder";
import BookingWaBus from "./BookingWaBus";
import BookingWaBank from "./BookingWaBank";
import BookingGallery from "./BookingGallery";

import ProfilePicture from "../Auth/ProfilePicture";
import { getBooking } from "../../store/booking/bookingActions";
import ChatTagBadges from "./ChatTagBadges";
import BookingwaDocs from "./BookingWaDocs";
import FileSelectionModal from "../Modals/FileSelectionModal";

export default function BookingForm({ setDoc, db, messagesEndRef, abspos, type, tag_id }) {
    const dispatch = useDispatch();
    const { current_staff, staff } = useSelector((state) => state.auth);
    const { current_booking } = useSelector((state) => state.booking);
    const { appointments } = useSelector((state) => state.appointments);
    const { settings } = useSelector((state) => state.settings);
    const [mentions, setMentions] = useState([]);
    const [allowMention, setAllowMention] = useState(false);
    const [fileTypes, setFileTypes] = useState([]);
    const [showFileModal, setShowFileModal] = useState(false);
    const [resetFilesSignal, setResetFilesSignal] = useState(0);
    const [expBlock, setExpBlock] = useState(null); // { until, reason }
    const [useMessagebird, setUseMessagebird] = useState(false);
    const [mbToggleLoading, setMbToggleLoading] = useState(false);

    const isQL = process.env.REACT_APP_COLOUR === "QL";

    useEffect(() => {
        if (current_booking?.id) {
            setUseMessagebird(!!current_booking.use_messagebird);
        }
    }, [current_booking?.id, current_booking?.use_messagebird]);

    const handleToggleMessagebird = async () => {
        if (!current_booking?.id || mbToggleLoading) return;
        setMbToggleLoading(true);
        try {
            const { data } = await axios.post(`${process.env.REACT_APP_API_URL}toggle_messagebird`, {
                booking_id: current_booking.id,
                staff_id: current_staff?.id,
            });
            setUseMessagebird(!!data.use_messagebird);
            dispatch(getBooking(current_booking.id));
        } catch (e) {
            console.error("Toggle messagebird failed", e);
            toast(<NotificationToast title="Toggle failed" message="Could not toggle Messagebird override" type="error" />);
        } finally {
            setMbToggleLoading(false);
        }
    };

    // const [whisperActive, setWhisperActive] = useState(false);

    let show_web_chat = false;

    if (current_booking?.chat_id > 0) {
        var now = new Date();
        var end = new Date(current_booking?.chat_timestamp);

        var diff = parseInt((Math.abs(end.getTime() - now.getTime()) / (1000 * 60)) % 60);
        var diffdays = Math.ceil(Math.abs(now - end) / (1000 * 60 * 60 * 24));

        // console.log(diffdays);
    }
    if (current_booking.assigned_to !== null && allowMention === false) {
        setAllowMention(true);
        setMentions(current_booking.assigned_to);
    }

    // Fetch experiment block flag for this booking
    useEffect(() => {
        const fetchBlock = async () => {
            try {
                if (current_booking?.id) {
                    const { data } = await axios.get(`${process.env.REACT_APP_API_URL}bookings/experiment-blocks`, {
                        params: { ids: String(current_booking.id) },
                    });
                    const blk = data?.blocked?.[current_booking.id];
                    setExpBlock(blk || null);
                } else {
                    setExpBlock(null);
                }
            } catch (e) {
                setExpBlock(null);
            }
        };
        fetchBlock();
    }, [current_booking?.id]);

    // Fetch file types based on student type
    useEffect(() => {
        const fetchFileTypes = async () => {
            try {
                // Get student type from current_booking
                // For parent bookings without student_type, use 0 to indicate parent/funder
                const studentType = current_booking?.student_type || (current_booking?.id ? 0 : null);
                let response;
                if (studentType !== null) {
                    try {
                        // Fetch file types for the specific student type (or parent if studentType = 0)
                        // Pass booking_id so backend can filter by year_of_study and funder status
                        const params = current_booking?.id ? { booking_id: current_booking.id } : {};
                        response = await axios.get(`${process.env.REACT_APP_API_URL}file-types/student-type/${studentType}`, { params });

                        // If we got a successful response but no file types, fall back to no file types
                        // if (!response.data || response.data.length === 0) {
                        //     response = await axios.get(`${process.env.REACT_APP_API_URL}file-types`);
                        // }
                    } catch (error) {
                        console.error("Error fetching file types for student type, falling back to all types:", error);
                        //   response = await axios.get(`${process.env.REACT_APP_API_URL}file-types`);
                        response = [];
                    }
                } else {
                    // No booking ID, fetch no file types
                    response = [];
                }

                // Transform and flatten the response data to get all file types
                const allFileTypes = Array.isArray(response.data)
                    ? response.data.flatMap((group) =>
                          Array.isArray(group.file_types)
                              ? group.file_types.map((ft) => ({
                                    ...ft,
                                    required: ft.required || false,
                                    groupName: group.group_name, // Keep group name for reference if needed
                                }))
                              : [],
                      )
                    : [];

                setFileTypes(allFileTypes);
            } catch (error) {
                console.error("Error fetching file types:", error);
                // Fallback to empty array in case of error
                setFileTypes([]);
            }
        };
        fetchFileTypes();
    }, [current_booking?.student_type]); // Re-run when student_type changes

    // if(callOnce === false){
    //     setCallOnce(true)
    //     appointments.forEach(function(appointment){
    //         if((appointment.property_id === current_booking.property_id) || ((current_booking.property_id === 7 || current_booking.property_id === 3) && appointment.property_id === 6)){

    //             if(appointment.name === "Move Out Inspection"){
    //                 setCustomMsg(window.location.origin+"/appointment/"+appointment.link_url+"/"+appointment.id+"/0/0/"+current_booking?.user.edit_url)
    //             }
    //         }
    //     })
    // }

    // console.log("current_staff", (window.localStorage.token).split("|")[1])

    // useEffect(() => {

    //     axios.defaults.baseURL = process.env.REACT_APP_API_URL;

    //     const echo = new Echo({
    //         broadcaster: "pusher",
    //         key: process.env.REACT_APP_MIX_ABLY_PUBLIC_KEY,

    //         wsHost: process.env.REACT_APP_MIX_ABLY_URL,
    //         wssHost: process.env.REACT_APP_MIX_ABLY_URL,

    //         wsPort: process.env.REACT_APP_MIX_ABLY_PORT,
    //         wssPort: process.env.REACT_APP_MIX_ABLY_PORT,

    //         disableStats: true,
    //         encrypted: false,
    //         enabledTransports: ["ws", "wss"],
    //         forceTLS: true,

    //         enableLogging: true,
    //         authEndpoint: process.env.REACT_APP_LARAVEL_URL+"broadcasting/auth",
    //         auth: {
    //             headers: {
    //                 Authorization: 'Bearer ' + window.localStorage.token
    //             },
    //         },
    //     });

    //     // console.log("ECHO", echo);

    //     // if(whisperActive === true){
    //         console.log("1")
    //     //     setWhisperActive(false)

    //         echo.private(`Typing.${current_booking.id}`)
    //             .whisper('Typing', {
    //                 name: 'one',
    //                 typing: true
    //             });

    //     // }
    //     // echo.private(`Typing.${current_booking.id}`)
    //     //     .listenForWhisper('Typing', (e) => {
    //     //         console.log(e.name);
    //     //     });

    //     return () => {
    //         echo.disconnect();
    //     };

    // }, [whisperActive])

    // Compute WhatsApp block state from API response or current booking fields as fallback
    const blockUntil = expBlock?.until || current_booking?.experiment_block_until;
    const whatsappBlocked = !!blockUntil && moment(blockUntil).isAfter(moment());

    // Email unsubscribe state — recipient has opted out of marketing email.
    // The send pipeline already refuses these sends server-side; this banner
    // surfaces the same state in the UI so operators don't waste a draft.
    const emailUnsubscribedAt = current_booking?.user?.email_unsubscribed_at;
    const emailUnsubscribed = !!emailUnsubscribedAt;

    return (
        <>
            {whatsappBlocked && (
                <div className="mb-3 rounded-md bg-red-50 p-3 border border-red-200 text-sm text-red-800">
                    WhatsApp sending is temporarily blocked by a Meta experiment until <strong>{moment(blockUntil).format("YYYY-MM-DD HH:mm")}</strong>. Replies from the user will automatically cancel
                    the rescheduled send and clear this notice.
                </div>
            )}
            {emailUnsubscribed && (
                <div className="mb-3 rounded-md bg-red-50 p-3 border border-red-200 text-sm text-red-800">
                    This recipient unsubscribed from email on <strong>{moment(emailUnsubscribedAt).format("YYYY-MM-DD HH:mm")}</strong>. Email sends to this user are blocked. Transactional sends (lease docs, AOS) can still be triggered through their dedicated flows.
                </div>
            )}
            <Formik
                enableReinitialize
                initialValues={{
                    message: "",
                    platform: "",
                    from: current_staff?.id,
                    to: "",
                    files: [],
                    template: 0,
                    temaplte_name: "",
                    booking: current_booking.id,
                    aos_unit: 0,
                    appointment_id: 0,
                    linked_chat_id: 0,
                }}
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={Yup.object().shape({
                    platform: Yup.string().required("Message platform is not selected"),
                    message: Yup.string().when("platform", {
                        is: (value) => value && value != "whatsapp",
                        then: Yup.string().required("Message is empty"),
                    }),
                })}
                onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
                    // Block WhatsApp sends while experiment block is active
                    if (values.platform === "whatsapp" && whatsappBlocked) {
                        toast.warning(`WhatsApp sending is temporarily blocked by Meta experiment until ${moment(blockUntil).format("YYYY-MM-DD HH:mm")}`);
                        setSubmitting(false);
                        return;
                    }
                    // Block email sends to recipients who have unsubscribed.
                    if (values.platform === "email" && emailUnsubscribed) {
                        toast.warning("This recipient has unsubscribed from email and cannot be contacted via this channel.");
                        setSubmitting(false);
                        return;
                    }
                    let multiChat = false;
                    let chatSendList = [
                        {
                            user_id: current_booking.user.id,
                            conversation_id: current_booking.user.conversation_id,
                            cell: values.to,
                            booking_id: current_booking.id,
                            chat_id: current_booking.chat_id,
                            name: current_booking.user.name,
                            surname: current_booking.user.surname,
                            email: current_booking.user.email,
                        },
                    ];

                    await axios({
                        method: "post",
                        url: `${process.env.REACT_APP_API_URL}get_merged_users`,
                        data: { booking_id: current_booking.id },
                    })
                        .then((response) => {
                            if (response.data.merged_bookings.length > 0) {
                                multiChat = true;
                                response.data.merged_bookings.forEach(function (data) {
                                    data.user_id = data?.user?.id;
                                    data.booking_id = data?.id;
                                    data.chat_id = data?.chat_id;
                                    chatSendList.push(data);
                                });
                            }
                        })
                        .catch((error) => {
                            alert("merged users load failed");
                        });

                    async function processArray(array) {
                        for (const item of array) {
                            // console.log(
                            //   "sending " + item.name + " " + item.surname
                            // );

                            if (
                                ((values.platform == "whatsapp" || values.platform == "whatsapp_hijack" || values.platform == "sms") && item.cell != null) ||
                                (values.platform == "email" && item.email != null) ||
                                values.platform == "website" ||
                                values.platform == "note"
                            ) {
                                const the_message = values.message;
                                var template = values.template;
                                var template_name = values.template_name;
                                let replaced_message = values.message;

                                let regards = "The Qliving Team";
                                if (process.env.REACT_APP_COLOUR === "UC") {
                                    regards = "The Urban Circle Team";
                                } else if (process.env.REACT_APP_COLOUR === "QP") {
                                    regards = "The Quorum Properties Team";
                                }

                                // replaced_message = the_message.replace("[name]", item.name);
                                replaced_message = replaced_message.replace("[me]", current_staff?.name);
                                replaced_message = replaced_message.replace("[regards]", "Kind Regards, " + regards);
                                // replaced_message = replaced_message.replace("[surname]", item.surname);
                                // replaced_message = replaced_message.replace("[email]", item.email);
                                // replaced_message = replaced_message.replace("[booking_reference]", current_booking?.booking_reference);
                                replaced_message = replaced_message.replace(
                                    "[edit_profile]",
                                    window.location.origin + "/edit_application/" + (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url),
                                );
                                replaced_message = replaced_message.replace("[building name]", current_booking?.property?.name);

                                replaced_message = replaced_message.replace("[room/bed]", current_booking?.unit?.name + " " + current_booking?.bed?.name);

                                replaced_message = replaced_message.replace("[unit_no]", current_booking?.unit?.name);

                                replaced_message = replaced_message.replace("[bank_details]", window.location.origin + "/bank-details/");
                                replaced_message = replaced_message.replace(
                                    "[payfast_link]",
                                    window.location.origin + "/payfast/" + (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url),
                                );
                                replaced_message = replaced_message.replace("[lease_link]", process.env.REACT_APP_LARAVEL_URL + "lease/" + current_booking?.lease_url);

                                replaced_message = replaced_message.replace("[lease]", process.env.REACT_APP_LARAVEL_URL + "lease/" + current_booking?.lease_url);

                                replaced_message = replaced_message.replace("[invoice_link]", process.env.REACT_APP_LARAVEL_URL + "invoice/" + current_booking?.invoice_url);
                                replaced_message = replaced_message.replace(
                                    "[documents_link]",
                                    window.location.origin + "/documents/" + (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url),
                                );
                                replaced_message = replaced_message.replace(
                                    "[move_in_form]",
                                    process.env.REACT_APP_LARAVEL_URL + "move-in-form/" + (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url),
                                );

                                // Hyperlink

                                replaced_message = replaced_message.replace(
                                    "[move_in_form_Hyperlink]",
                                    "<a href='" +
                                        process.env.REACT_APP_LARAVEL_URL +
                                        "move-in-form/" +
                                        (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                        "'>Move In Form</a>",
                                );

                                replaced_message = replaced_message.replace(
                                    "[Move_In_Inspection_Hyperlink]",
                                    current_booking?.property_id == 1
                                        ? "<a href='" +
                                              window.location.origin +
                                              "/appointment/Move_In_Inspection/17/0/0/" +
                                              (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                              "'>Move In Inspection</a>"
                                        : current_booking?.property_id == 2
                                          ? "<a href='" +
                                            window.location.origin +
                                            "/appointment/Move_In_Inspection/16/0/0/" +
                                            (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                            "'>Move In Inspection</a>"
                                          : current_booking?.property_id == 4
                                            ? "<a href='" +
                                              window.location.origin +
                                              "/appointment/Move_In_Inspection/15/0/0/" +
                                              (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                              "'>Move In Inspection</a>"
                                            : current_booking?.property_id == 3 || current_booking?.property_id == 6 || current_booking?.property_id == 7
                                              ? "<a href='" +
                                                window.location.origin +
                                                "/appointment/Move_In_Inspection/18/0/0/" +
                                                (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                                "'>Move In Inspection</a>"
                                              : current_booking?.property_id == 8
                                                ? "<a href='" +
                                                  window.location.origin +
                                                  "/appointment/Move_In_Inspection/27/0/0/" +
                                                  (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                                  "'>Move In Inspection</a>"
                                                : "",
                                );

                                // [Move_In_Inspection] is now handled by the backend via MessageShortcodeHelper

                                replaced_message = replaced_message.replace(
                                    "[Move_Out_Inspection]",
                                    current_booking?.property_id == 1
                                        ? window.location.origin +
                                              "/appointment/Move_Out_Inspection/13/0/0/" +
                                              (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url)
                                        : current_booking?.property_id == 2
                                          ? window.location.origin +
                                            "/appointment/Move_Out_Inspection/12/0/0/" +
                                            (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url)
                                          : current_booking?.property_id == 4
                                            ? window.location.origin +
                                              "/appointment/Move_Out_Inspection/11/0/0/" +
                                              (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url)
                                            : current_booking?.property_id == 3 || current_booking?.property_id == 6 || current_booking?.property_id == 7
                                              ? window.location.origin +
                                                "/appointment/Move_Out_Inspection/14/0/0/" +
                                                (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url)
                                              : "",
                                );
                                replaced_message = replaced_message.replace(
                                    "[Move_Out_Inspection_Hyperlink]",
                                    current_booking?.property_id == 1
                                        ? "<a href='" +
                                              window.location.origin +
                                              "/appointment/Move_Out_Inspection/13/0/0/" +
                                              (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                              "'>Move Out Inspection</a>"
                                        : current_booking?.property_id == 2
                                          ? "<a href='" +
                                            window.location.origin +
                                            "/appointment/Move_Out_Inspection/12/0/0/" +
                                            (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                            "'>Move Out Inspection</a>"
                                          : current_booking?.property_id == 4
                                            ? "<a href='" +
                                              window.location.origin +
                                              "/appointment/Move_Out_Inspection/11/0/0/" +
                                              (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                              "'>Move Out Inspection</a>"
                                            : current_booking?.property_id == 3 || current_booking?.property_id == 6 || current_booking?.property_id == 7
                                              ? "<a href='" +
                                                window.location.origin +
                                                "/appointment/Move_Out_Inspection/14/0/0/" +
                                                (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                                "'>Move Out Inspection</a>"
                                              : "",
                                );

                                replaced_message = replaced_message.replace(
                                    "[Viewing]",
                                    current_booking?.property_id == 1
                                        ? window.location.origin +
                                              "/appointment/Viewing/7/0/0/" +
                                              (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url)
                                        : current_booking?.property_id == 2
                                          ? window.location.origin +
                                            "/appointment/Viewing/9/0/0/" +
                                            (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url)
                                          : current_booking?.property_id == 4
                                            ? window.location.origin +
                                              "/appointment/Viewing/8/0/0/" +
                                              (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url)
                                            : current_booking?.property_id == 3 || current_booking?.property_id == 6 || current_booking?.property_id == 7
                                              ? window.location.origin +
                                                "/appointment/Viewing/10/0/0/" +
                                                (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url)
                                              : current_booking?.property_id == 8
                                                ? window.location.origin +
                                                  "/appointment/Viewing/25/0/0/" +
                                                  (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url)
                                                : "",
                                );

                                replaced_message = replaced_message.replace(
                                    "[Viewing | The Ox]",
                                    window.location.origin +
                                        "/appointment/viewing/7/0/0/" +
                                        (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url),
                                );
                                replaced_message = replaced_message.replace(
                                    "[Viewing | Forest Views]",
                                    window.location.origin +
                                        "/appointment/viewing/9/0/0/" +
                                        (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url),
                                );
                                replaced_message = replaced_message.replace(
                                    "[Viewing | Delve Deeper]",
                                    window.location.origin +
                                        "/appointment/viewing/8/0/0/" +
                                        (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url),
                                );
                                replaced_message = replaced_message.replace(
                                    "[Viewing | The Unison]",
                                    window.location.origin +
                                        "/appointment/viewing/10/0/0/" +
                                        (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url),
                                );
                                replaced_message = replaced_message.replace(
                                    "[Viewing | Homage]",
                                    window.location.origin +
                                        "/appointment/viewing/25/0/0/" +
                                        (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url),
                                );
                                replaced_message = replaced_message.replace(
                                    "[Viewing | Kent]",
                                    window.location.origin +
                                        "/appointment/viewing/28/0/0/" +
                                        (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url),
                                );

                                replaced_message = replaced_message.replace(
                                    "[Documents_Hyperlink]",
                                    "<a href='" +
                                        window.location.origin +
                                        "/documents/" +
                                        (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                        "'>Documents</a>",
                                );

                                replaced_message = replaced_message.replace(
                                    "[Edit_Profile_Hyperlink]",
                                    "<a href='" +
                                        window.location.origin +
                                        "/edit_application/" +
                                        (current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url) +
                                        "'>Edit Your Profile</a>",
                                );

                                replaced_message = replaced_message.replace(
                                    "[Invoice_Hyperlink]",
                                    "<a href='" + process.env.REACT_APP_LARAVEL_URL + "invoice/" + current_booking?.invoice_url + "'>Invoice</a>",
                                );

                                replaced_message = replaced_message.replace(
                                    "[Lease_Hyperlink]",
                                    "<a href='" + process.env.REACT_APP_LARAVEL_URL + "lease/" + current_booking?.lease_url + "'>Lease</a>",
                                );

                                // replaced_message = replaced_message.replace("[move_in_inspection]", current_booking?.property_id == 1 ? "https://calendly.com/theox-1/move-in-inspection" : current_booking?.property_id == 2  ? "https://calendly.com/forestviews/30min-1" : current_booking?.property_id == 4  ? "https://calendly.com/delvedeeper/move-in-inspection" : current_booking?.property_id == 3 || current_booking?.property_id == 6 || current_booking?.property_id == 7 ? "https://calendly.com/the-unison/move-in-inspection" : "");

                                // replaced_message = replaced_message.replace("[move_out_inspection]",  current_booking?.property_id == 1 ? "https://calendly.com/theox-1/move-out-inspection" : current_booking?.property_id == 2 ? "https://calendly.com/forestviews/move-out-inspection" : current_booking?.property_id == 4 ? "https://calendly.com/delvedeeper/move-out-inspection" : current_booking?.property_id == 3 || current_booking?.property_id == 6 || current_booking?.property_id == 7 ? "https://calendly.com/the-unison/move-out" : "");

                                // replaced_message = replaced_message.replace("[book_a_viewing]", current_booking?.property_id == 1 ? "https://calendly.com/theox-1/viewing-at-the-ox" : current_booking?.property_id == 2 ? "https://calendly.com/forestviews/book-a-viewing" : current_booking?.property_id == 4 ? "https://calendly.com/delvedeeper/viewings" : current_booking?.property_id == 3 || current_booking?.property_id == 6 || current_booking?.property_id == 7 ? "https://calendly.com/the-unison/30min" : "");

                                // replaced_message = replaced_message.replace("[move_out_custom_inspection]", customMsg);

                                var form_data = new FormData();

                                var timestamp = new Date();
                                var platform = values.platform;
                                var from = current_staff?.id;
                                var to = values.to;
                                var user_id = item.id;
                                var files = [];

                                form_data.append("timestamp", timestamp);
                                form_data.append("message", replaced_message);
                                form_data.append("conversation_id", item.conversation_id);
                                form_data.append("platform", platform);
                                form_data.append("from", from);
                                form_data.append("booking", item.booking_id);
                                form_data.append("to", item.cell);
                                form_data.append("user_id", item.user_id);
                                form_data.append("staff_id", current_staff.id);
                                form_data.append("template", template);
                                form_data.append("template_name", template_name);
                                form_data.append("type", type);
                                form_data.append("tag_id", tag_id);
                                form_data.append("aos_unit", values.aos_unit);
                                form_data.append("appointment_id", values.appointment_id);
                                form_data.append("linked_chat_id", values.linked_chat_id);

                                if (type === "collections") {
                                    form_data.append("collections", 1);
                                }

                                if (type === "renewals") {
                                    form_data.append("renewals", 1);
                                }

                                //Check if this is a website message, add Chat ID
                                if (platform === "website" && item.chat_id) {
                                    form_data.append("chat_id", item.chat_id);
                                }

                                // Helper: convert various inputs to a real File via fetch when needed
                                async function toFileIfNeeded(f) {
                                    try {
                                        // If it's already a File/Blob, return as-is
                                        if ((typeof File !== "undefined" && f instanceof File) || (typeof Blob !== "undefined" && f instanceof Blob)) {
                                            return f;
                                        }

                                        // If we received an object with a URL/path, fetch and convert
                                        if (typeof f === "object" && f !== null) {
                                            const fetchUrl = f.path || f.url;
                                            if (fetchUrl) {
                                                const res = await fetch(fetchUrl);
                                                const blob = await res.blob();
                                                const name = f.name || f.filename || "attachment";
                                                return new File([blob], name, { type: blob.type || "application/octet-stream" });
                                            }
                                            return f;
                                        }

                                        // If it's a JSON string, parse and fetch
                                        if (typeof f === "string") {
                                            const obj = JSON.parse(f);
                                            const fetchUrl = obj?.path || obj?.url;
                                            if (!fetchUrl) return f; // nothing to fetch
                                            const res = await fetch(fetchUrl);
                                            const blob = await res.blob();
                                            const name = obj?.name || "attachment";
                                            return new File([blob], name, { type: blob.type || "application/octet-stream" });
                                        }

                                        return f;
                                    } catch (e) {
                                        console.error("toFileIfNeeded failed", e);
                                        return f;
                                    }
                                }

                                // If a template is selected, backend expects JSON strings with {name, path}
                                const isWhatsApp = values.platform === "whatsapp" || values.platform === "whatsapp_hijack";
                                if (template > 0) {
                                    if (values.files && values.files.length > 0) {
                                        const makeJson = (entry) => {
                                            if (typeof entry === "string") {
                                                try {
                                                    const obj = JSON.parse(entry);
                                                    return JSON.stringify({
                                                        name: obj?.name || obj?.filename || "attachment",
                                                        path: obj?.path || obj?.url || "",
                                                    });
                                                } catch (e) {
                                                    return JSON.stringify({ name: "attachment", path: "" });
                                                }
                                            } else {
                                                return JSON.stringify({ name: entry?.name || "attachment", path: "" });
                                            }
                                        };

                                        if (isWhatsApp || values.files.length === 1) {
                                            const payload = makeJson(values.files[0]);
                                            form_data.append("files", payload);
                                            files = JSON.parse(payload)?.name;
                                        } else {
                                            files = [];
                                            for (const f of values.files) {
                                                const payload = makeJson(f);
                                                form_data.append("files[]", payload);
                                                try {
                                                    files.push(JSON.parse(payload)?.name);
                                                } catch {}
                                            }
                                        }
                                    }
                                } else {
                                    // No template => backend expects actual uploaded files
                                    if (values.files && values.files.length > 0) {
                                        if (isWhatsApp || values.files.length === 1) {
                                            let f0 = await toFileIfNeeded(values.files[0]);
                                            // Unwrap booking uploader shape {file, file_type_id}
                                            if (f0 && typeof f0 === "object" && !("slice" in f0) && f0.file) {
                                                f0 = f0.file;
                                            }
                                            form_data.append("files", f0);
                                            files = (f0 && (f0.name || f0.filename)) || files;
                                        } else {
                                            files = [];
                                            for (const f of values.files) {
                                                let fx = await toFileIfNeeded(f);
                                                if (fx && typeof fx === "object" && !("slice" in fx) && fx.file) {
                                                    fx = fx.file;
                                                }
                                                form_data.append("files[]", fx);
                                                files.push(fx?.name || fx?.filename || "(no name)");
                                            }
                                        }
                                    }
                                }

                                console.log("values:", values);
                                console.log("files (names/log):", files);
                                for (const [k, v] of form_data.entries()) {
                                    console.log("FormData:", k, v);
                                }

                                if (values.platform == "note") {
                                    if (replaced_message.includes("@")) {
                                        if (!replaced_message.includes("wysiwyg-mention")) {
                                            alert("Mention not linked");
                                            return false;
                                        }
                                    }
                                }

                                // Add hijacked_user_id to form data if this is a hijacked message
                                if (values.platform === "whatsapp_hijack" && current_booking.user?.hijacked_user_id) {
                                    form_data.append("hijacked_user_id", current_booking.user.hijacked_user_id);
                                }

                                await axios
                                    .post(`${process.env.REACT_APP_API_URL}send`, form_data)
                                    .then((response) => {
                                        toast(<NotificationToast title={values.platform + " successful"} message={"Your message has been sent."} />);
                                        resetForm();
                                        // Clear any file-uploader internal state/UI
                                        setResetFilesSignal((s) => s + 1);
                                    })
                                    .catch((error) => {
                                        console.log(error);
                                        toast(<NotificationToast title="Something went wrong" message={JSON.stringify(error.response.data)} type="error" />, {
                                            autoClose: false,
                                        });
                                    });
                            }
                        }
                    }

                    await processArray(chatSendList);

                    setSubmitting(false);
                }}
            >
                {({ values, errors, setFieldValue, handleSubmit, isValid, isSubmitting, dirty }) => (
                    <Form onSubmit={handleSubmit} className="rounded-3xl w-full">
                        {/* Booking Notes Display - inside form so it's positioned correctly */}
                        {current_booking?.notes && (
                            <div className="mx-2 mb-2 rounded-md bg-yellow-50 p-3 border border-yellow-200 relative group transition-all duration-200">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 overflow-hidden">
                                        <div className="flex items-start space-x-2">
                                            <FontAwesomeIcon icon={faStickyNote} className="h-4 w-4 text-yellow-600 mt-1 flex-shrink-0" />
                                            <div className="flex-1">
                                                <div className="text-sm text-yellow-800 prose prose-sm max-w-none line-clamp-1 group-hover:line-clamp-none 2xl:line-clamp-none transition-all duration-200" dangerouslySetInnerHTML={{ __html: current_booking.notes }} />
                                                {(current_booking.notes_updated_by || current_booking.notes_updated_at) && (
                                                    <div className="text-xs text-yellow-600 mt-1">
                                                        {(current_booking.notes_updated_by?.name || current_booking.notes_updated_by?.surname) && (
                                                            <span>
                                                                Updated by {current_booking.notes_updated_by.name} {current_booking.notes_updated_by.surname}
                                                            </span>
                                                        )}
                                                        {current_booking.notes_updated_at && (
                                                            <span>
                                                                {(current_booking.notes_updated_by?.name || current_booking.notes_updated_by?.surname) && " • "}
                                                                {moment(current_booking.notes_updated_at).format("MMM D, YYYY h:mm A")}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="ml-2 text-yellow-600 hover:text-yellow-800 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                        onClick={() => {
                                            dispatch(
                                                openModal({
                                                    modal_type: "ModalBookingNotes",
                                                    modal_props: {
                                                        booking_id: current_booking.id,
                                                        current_notes: current_booking.notes,
                                                        current_staff: current_staff,
                                                    },
                                                }),
                                            );
                                        }}
                                        title="Edit Notes"
                                    >
                                        <FontAwesomeIcon icon={faStickyNote} className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                        {/* Messagebird/Meta toggle - show if global enabled OR booking already on Messagebird */}
                        {current_booking?.id && (settings?.messagebird_override_enabled || useMessagebird) && (
                            <div className="mx-2 mb-1 flex items-center justify-between rounded-md bg-gray-50 px-3 py-1.5 border border-gray-200">
                                <div className="flex items-center space-x-2">
                                    <button
                                        type="button"
                                        disabled={mbToggleLoading}
                                        onClick={handleToggleMessagebird}
                                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                            useMessagebird ? "bg-orange-500" : "bg-gray-300"
                                        } ${mbToggleLoading ? "opacity-50" : ""}`}
                                    >
                                        <span
                                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                useMessagebird ? "translate-x-4" : "translate-x-0"
                                            }`}
                                        />
                                    </button>
                                    <span className="text-xs text-gray-600">
                                        {useMessagebird ? "Sending via Messagebird (old number)" : "Sending via Meta (new number)"}
                                    </span>
                                </div>
                                {current_booking.last_inbound_provider === "messagebird" && (
                                    <span className="text-xs text-amber-600">
                                        Last reply from old number
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="bg-dark_background p-2 flex justify-between items-center -mb-7">
                            <div className="flex items-center space-x-4">
                                {mentions && mentions.length > 0 && (
                                    <div className="flex">
                                        {mentions.map((mention) => (
                                            <ProfilePicture key={"mention" + mention} id={mention} size="40" className="-mr-6" />
                                        ))}
                                    </div>
                                )}
                                {current_booking?.id && (
                                    <ChatTagBadges bookingId={current_booking.id} />
                                )}
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                    {fileTypes.slice(0, 5).map((fileType, index) => {
                                        // Default to red X (no file uploaded or only declined files)
                                        let icon = faX;
                                        let color = "#ef4444"; // red-500
                                        let tooltip = "Not uploaded";

                                        // Get all matching files for this file type (there could be multiple attempts)
                                        let matchingFiles = current_booking?.files?.filter((file) => file.file_type_id === fileType.id) || [];

                                        // Filter out declined files first
                                        let validFiles = matchingFiles.filter((file) => file.status !== "Declined");

                                        // Special case: If this is ID requirement and user has passport number (not SA ID),
                                        // check if they have an approved Passport document (only if no valid ID files exist)
                                        if (validFiles.length === 0 && fileType.name === "ID") {
                                            const userIdNumber = current_booking?.user?.profile?.id_number;

                                            if (userIdNumber) {
                                                // Clean ID number (remove Unicode characters and whitespace)
                                                const cleanedIdNumber = userIdNumber.replace(/[\u200E\u200F\u202A-\u202E\s]/g, "");

                                                // Check if NOT a 13-digit SA ID (meaning it's likely a passport number)
                                                const isPassportNumber = !/^\d{13}$/.test(cleanedIdNumber);

                                                if (isPassportNumber) {
                                                    // Look for Passport files directly in booking files by checking file_type field
                                                    // We check ALL files, not just those in fileTypes (since Passport may not be required)
                                                    const passportFiles =
                                                        current_booking?.files?.filter((file) => {
                                                            // Check if file_type_id matches Passport (ID=4) or file has "passport" in the name
                                                            return file.file_type_id === 4; // Passport file type ID
                                                        }) || [];

                                                    // Use passport files as valid files for ID requirement
                                                    validFiles = passportFiles.filter((file) => file.status !== "Declined");
                                                }
                                            }
                                        }

                                        // Check if document is not yet required
                                        // Logic:
                                        // 1. If required_date exists: It is "not yet required" ONLY if date is in future.
                                        //    (If date is past, it becomes required/missing, regardless of 'required' flag)
                                        // 2. If NO required_date: It is "not yet required" if 'required' is false (optional).
                                        let isNotYetRequired = false;

                                        if (fileType.required_date) {
                                            isNotYetRequired = moment(fileType.required_date).isAfter(moment());
                                        } else {
                                            isNotYetRequired = !fileType.required;
                                        }

                                        if (isNotYetRequired && validFiles.length === 0) {
                                            // Clock icon for not-yet-required documents (only if not uploaded)
                                            icon = faClock;
                                            color = "#6b7280"; // gray-500
                                            if (fileType.required_date && moment(fileType.required_date).isAfter(moment())) {
                                                tooltip = `Required from ${moment(fileType.required_date).format("MMM DD, YYYY")}`;
                                            } else {
                                                tooltip = "Optional";
                                            }
                                        } else if (validFiles.length > 0) {
                                            // Check if any file is approved
                                            const hasApproved = validFiles.some((file) => file.status === "Approved" || file.status === "Approve");

                                            if (hasApproved) {
                                                // Green check for approved files
                                                icon = faCheck;
                                                color = "#22c55e"; // green-500
                                                tooltip = "Approved";
                                            } else {
                                                // Orange circle for pending files (not declined, not approved)
                                                icon = faCircle;
                                                color = "#f97316"; // orange-500
                                                tooltip = "Pending approval";
                                            }
                                        }

                                        return (
                                            <div key={`${fileType.id}-${index}`} className="flex items-center text-white rounded-full text-xs whitespace-nowrap group relative" title={fileType.name}>
                                                <FontAwesomeIcon icon={icon} className="mr-1" style={{ color }} />
                                                {fileType.short_name || fileType.name}
                                                <span className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap z-10">
                                                    {fileType.name} - {tooltip}
                                                </span>
                                            </div>
                                        );
                                    })}
                                    {fileTypes.length > 5 && (
                                        <div key="more-files" className="text-xs text-gray-400">
                                            +{fileTypes.length - 5} more
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="cursor-pointer ml-4 relative inline-block text-white bg-primary px-2 py-1 rounded-xl hover:bg-primary_hover transition duration-150 ease-in-ease-out hover:-translate-y-0.5"
                                    onClick={() => {
                                        dispatch(
                                            openModal({
                                                modal_type: "SlideoverFiles",
                                                modal_props: {
                                                    type: "booking",
                                                    current_booking: current_booking,
                                                },
                                            }),
                                        );
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPaperclipVertical} className="h-4 w-4" aria-hidden="true" />
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-between items-end pr-6 bg-dark_background pt-8">
                            <div className="flex pl-6 space-x-4 h-8">
                                {/* Show Bolt button only if user is hijacked */}
                                {current_booking?.user?.hijacked_user_id && (
                                    <div className="mr-8">
                                        <BookingPlatformButton
                                            valuePlatform={values.platform}
                                            buttonPlatform={"whatsapp_hijack"} // Different platform value for hijack
                                            icon={faBoltLightning}
                                            message={"Hijack WhatsApp"}
                                            from={process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER}
                                            to={current_booking.user.cell}
                                            setFieldValue={setFieldValue}
                                            isHijacked={true}
                                            hijackedBy={current_booking.user.hijacker_name || "Admin"}
                                        />
                                    </div>
                                )}

                                {current_booking?.user?.cell && !current_staff.building_manager && (
                                    <>
                                        <BookingPlatformButton
                                            valuePlatform={values.platform}
                                            buttonPlatform={"whatsapp"}
                                            icon={faWhatsapp}
                                            message={"WhatsApp"}
                                            from={process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER}
                                            to={current_booking.user.cell}
                                            setFieldValue={setFieldValue}
                                        />
                                        <BookingPlatformButton
                                            valuePlatform={values.platform}
                                            buttonPlatform={"sms"}
                                            icon={faMessageLines}
                                            message={"SMS"}
                                            from={process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER}
                                            to={current_booking.user.cell}
                                            setFieldValue={setFieldValue}
                                        />
                                    </>
                                )}
                                {current_booking?.user?.email && !current_staff.building_manager && (
                                    <BookingPlatformButton
                                        valuePlatform={values.platform}
                                        buttonPlatform={"email"}
                                        icon={faEnvelope}
                                        message={"Email"}
                                        from={process.env.REACT_APP_FROM_EMAIL}
                                        to={current_booking.user.email}
                                        setFieldValue={setFieldValue}
                                    />
                                )}
                                <BookingPlatformButton
                                    valuePlatform={values.platform}
                                    buttonPlatform={"note"}
                                    icon={faNote}
                                    message={"Note"}
                                    from={current_staff?.id}
                                    to={""}
                                    setFieldValue={setFieldValue}
                                />
                                {current_booking?.chat_id > 0 && diffdays === 1 && diff < 15 && !current_staff.building_manager && (
                                    <BookingPlatformButton
                                        valuePlatform={values.platform}
                                        buttonPlatform={"website"}
                                        icon={faBrowser}
                                        message={"Website"}
                                        from={current_staff?.id}
                                        to={""}
                                        setFieldValue={setFieldValue}
                                    />
                                )}
                            </div>

                            {current_booking.bot_flow_id > 0 && <div className="text-center font-bold text-red-500">Currently in a Bot Flow</div>}

                            <div>{values.platform && <BookingTemplate platform={values.platform} setFieldValue={setFieldValue} handleSubmit={handleSubmit} current_booking={current_booking} />}</div>
                        </div>
                        {values.platform === "" && <div className="px-6 py-4 block w-full sm:text-sm !outline-none resize-none bg-light_background h-full">Please choose a Platform to send from</div>}
                        {(values.platform === "email" || values.platform === "note") && (
                            <BookingWysiwyg name="message" id="message" initialValues={values} setFieldValue={setFieldValue} staff={staff} value={values.message} />
                        )}
                        {(values.platform === "whatsapp" || values.platform === "whatsapp_hijack" || values.platform === "sms" || values.platform === "website") && (
                            <BookingTextArea as="textarea" rows={5} name="message" id="message" placeholder="Write your Message" errors={errors} />
                            // <BookingTextArea as="textarea" rows={5} name="message" id="message" placeholder="Write your Message" errors={errors} onKeyDown={() => setWhisperActive(true)} />
                        )}
                        {errors.message && (
                            <div className="px-6 inline-flex bg-light_background w-full">
                                <p>&nbsp;</p>
                                {errors.message && <p className="text-sm text-red-400 mr-4">{errors.message}</p>}
                            </div>
                        )}
                        <div className={`px-6 ml-auto flex justify-between items-center space-x-5 bg-dark_background rounded-b-3xl ${values.files && values.files.length > 0 ? 'py-4 pb-12' : 'py-4'}`}>
                            <div className="flex">
                                <EmojiPicker value={values.message} source="booking" setFieldValue={setFieldValue} />
                                {values.platform !== "sms" && (
                                    <BookingFileUpload
                                        setFieldValue={setFieldValue}
                                        values={values.files}
                                        max_files={values.platform === "whatsapp" ? 1 : ""}
                                        fileTypes={fileTypes}
                                        resetSignal={resetFilesSignal}
                                    />
                                )}
                                <BookingPlaceholder setFieldValue={setFieldValue} value={values.message} platform={values.platform} />
                                {(values.platform === "whatsapp" || values.platform === "whatsapp_hijack") && (
                                    <>
                                        <BookingWaBus setFieldValue={setFieldValue} values={values} />
                                        <BookingGallery setFieldValue={setFieldValue} values={values} />
                                    </>
                                )}

                                {process.env.REACT_APP_HIDE_CHAT_TABS && process.env.REACT_APP_HIDE_CHAT_TABS === "true" ? (
                                    <BookingwaDocs setFieldValue={setFieldValue} />
                                ) : (
                                    <BookingWaBank setFieldValue={setFieldValue} />
                                )}
                            </div>
                            <div className="flex items-center space-x-3 pr-4">
                                <button
                                    type="button"
                                    className={`cursor-pointer relative inline-block text-white px-2 py-1 rounded-xl transition duration-150 ease-in-ease-out hover:-translate-y-0.5 ${current_booking?.notes ? "bg-yellow-500 hover:bg-yellow-600" : "bg-primary hover:bg-primary_hover"}`}
                                    onClick={() => {
                                        dispatch(
                                            openModal({
                                                modal_type: "ModalBookingNotes",
                                                modal_props: {
                                                    booking_id: current_booking.id,
                                                    current_notes: current_booking.notes || "",
                                                    current_staff: current_staff,
                                                },
                                            }),
                                        );
                                    }}
                                    title={current_booking?.notes ? "Edit Booking Notes" : "Add Booking Notes"}
                                >
                                    <FontAwesomeIcon icon={faStickyNote} className="h-4 w-4" aria-hidden="true" />
                                </button>
                                {values.platform !== "" &&
                                    (values.platform === "whatsapp" && whatsappBlocked ? (
                                        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                                            WhatsApp sending is blocked until {moment(blockUntil).format("YYYY-MM-DD HH:mm")}.
                                        </div>
                                    ) : values.platform === "email" && emailUnsubscribed ? (
                                        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                                            Recipient unsubscribed from email — sending is blocked.
                                        </div>
                                    ) : (
                                        <SubmitButton name="Submit" disabled={!isValid || isSubmitting || !dirty} isSubmitting={isSubmitting} text="Send" />
                                    ))}
                            </div>
                        </div>

                        {/* File Selection Modal */}
                        <FileSelectionModal
                            isOpen={showFileModal}
                            onClose={() => setShowFileModal(false)}
                            context={{ type: "booking", current_booking }}
                            onAttach={(selected) => {
                                try {
                                    const mapped = (selected || [])
                                        .map((f) => {
                                            const name = f.name || f.original_name || `file-${f.id}`;
                                            const rawPath = f.path || "";
                                            const base = process.env.REACT_APP_R2_DEVURL || "";
                                            // Do not encode slashes; R2 public URLs require normal path separators
                                            const fullUrl = rawPath && base ? `${base}/public/${rawPath}` : "";
                                            // Store as {name, path} where path is a full URL
                                            return JSON.stringify({ name, path: fullUrl });
                                        })
                                        .filter(Boolean);

                                    if (!Array.isArray(values.files)) {
                                        values.files = [];
                                    }

                                    // Enforce WhatsApp single-file rule
                                    let newFiles = [...values.files, ...mapped];
                                    if (values.platform === "whatsapp" || values.platform === "whatsapp_hijack") {
                                        if (newFiles.length > 1) {
                                            newFiles = [newFiles[0]];
                                            toast(<NotificationToast title="WhatsApp allows 1 attachment" message="Limited to the first selected/attached file." />);
                                        }
                                    }

                                    setFieldValue("files", newFiles);
                                } catch (e) {
                                    console.error("Attach from modal failed", e);
                                } finally {
                                    setShowFileModal(false);
                                }
                            }}
                        />
                    </Form>
                )}
            </Formik>
        </>
    );
}
